import React from 'react';
import { DocsLayout } from '../components/DocsLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Sun, Moon, Monitor, Eye, Battery, Zap, Info, CheckCircle, FileText } from 'lucide-react';

export const DarkModeGuidePage: React.FC = () => {
  return (
    <DocsLayout
      title='Dark Mode'
      description='Switch between light and dark themes for comfortable viewing'
      breadcrumbs={[
        { label: 'Documentation', path: '/docs' },
        { label: 'Features', path: '/docs/features' },
        { label: 'Dark Mode' }
      ]}
    >
      <div className='max-w-[1000px]'>
        {/* Overview Section */}
        <section className='mb-20' id='overview'>
          <div className='bg-blue-50 dark:bg-blue-950/30 border-l-4 border-blue-600 p-6 rounded-r-xl mb-10'>
            <div className='flex gap-4'>
              <Info className='h-6 w-6 text-blue-600 flex-shrink-0 mt-1' />
              <div>
                <h3 className='text-lg font-semibold text-blue-900 dark:text-blue-100 mb-3'>
                  What is Dark Mode?
                </h3>
                <p className='text-sm leading-6 text-blue-800 dark:text-blue-200'>
                  Dark Mode changes the colors of Vehix from a light background (white) with dark
                  text to a dark background (black or dark gray) with light text. It is like
                  switching from reading a book in daylight to reading with a comfortable lamp at
                  night - easier on your eyes in certain situations.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className='mb-20' id='why-would-i-want-dark-mode'>
          <h2 className='text-3xl font-bold mb-6'>Why Would I Want Dark Mode?</h2>
          <p className='text-sm text-muted-foreground mb-10 leading-6'>
            Different people prefer different color schemes, and the same person might want
            different themes at different times of day. Here are the main benefits:
          </p>

          <div className='grid gap-6 sm:grid-cols-2'>
            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <div className='flex items-center gap-4 mb-2'>
                  <div className='p-3 bg-primary/10 rounded-xl'>
                    <Eye className='h-8 w-8 text-primary' />
                  </div>
                  <CardTitle className='text-lg font-bold'>Easier on Your Eyes</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6 text-muted-foreground'>
                  If you work in a room with dim lighting or at night, dark mode reduces the bright
                  glare from your screen. This can help prevent eye strain and headaches during long
                  work sessions.
                </p>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <div className='flex items-center gap-4 mb-2'>
                  <div className='p-3 bg-primary/10 rounded-xl'>
                    <Battery className='h-8 w-8 text-primary' />
                  </div>
                  <CardTitle className='text-lg font-bold'>Save Battery Life</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6 text-muted-foreground'>
                  If you are using Vehix on a laptop or tablet, dark mode can extend your battery
                  life by reducing how much power your screen uses. This is especially helpful when
                  you are working in the field without easy access to charging.
                </p>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <div className='flex items-center gap-4 mb-2'>
                  <div className='p-3 bg-primary/10 rounded-xl'>
                    <Moon className='h-8 w-8 text-primary' />
                  </div>
                  <CardTitle className='text-lg font-bold'>Better in Dark Environments</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6 text-muted-foreground'>
                  Working late at night or in a dark room? Dark mode does not light up the whole
                  room like a bright screen does. Your eyes will thank you, and you won't disturb
                  people around you.
                </p>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <div className='flex items-center gap-4 mb-2'>
                  <div className='p-3 bg-primary/10 rounded-xl'>
                    <Zap className='h-8 w-8 text-primary' />
                  </div>
                  <CardTitle className='text-lg font-bold'>Personal Preference</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6 text-muted-foreground'>
                  Some people simply prefer the look and feel of dark mode. It can make text and
                  buttons stand out more clearly and feel more modern. Try both and see what works
                  best for you!
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* How to Switch Section */}
        <section className='mb-20' id='how-to-switch-themes'>
          <h2 className='text-3xl font-bold mb-6 flex items-center gap-3'>
            <Sun className='h-8 w-8 text-primary' />
            How to Switch Themes
          </h2>

          <div className='mb-12'>
            <h3 className='text-base font-semibold mb-6'>
              Method 1: Using the Theme Toggle Button (Fastest)
            </h3>
            <p className='text-sm text-muted-foreground mb-10 leading-6'>
              This is the quickest way to switch themes:
            </p>

            <div className='space-y-6'>
              <div className='relative flex gap-4'>
                <div className='flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg'>
                  1
                </div>
                <div className='absolute left-6 top-12 bottom-0 w-0.5 bg-border -translate-x-1/2' />
                <div className='flex-1 pb-2'>
                  <h4 className='text-lg font-semibold text-foreground mb-2'>
                    Find the Theme Icon
                  </h4>
                  <p className='text-sm leading-6 text-muted-foreground'>
                    Look in the sidebar (left side of screen) for a sun or moon icon. The sun means
                    you are in light mode, the moon means you are in dark mode.
                  </p>
                </div>
              </div>

              <div className='relative flex gap-4'>
                <div className='flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg'>
                  2
                </div>
                <div className='absolute left-6 top-12 bottom-0 w-0.5 bg-border -translate-x-1/2' />
                <div className='flex-1 pb-2'>
                  <h4 className='text-lg font-semibold text-foreground mb-2'>Click to Switch</h4>
                  <p className='text-sm leading-6 text-muted-foreground'>
                    Click the icon once to cycle through the options: Light → Dark → System. The
                    page will change instantly.
                  </p>
                </div>
              </div>

              <div className='relative flex gap-4'>
                <div className='flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg'>
                  3
                </div>
                <div className='flex-1 pb-2'>
                  <h4 className='text-lg font-semibold text-foreground mb-2'>Pick Your Favorite</h4>
                  <p className='text-sm leading-6 text-muted-foreground'>
                    Keep clicking until you find the mode you like best. Your choice will be
                    remembered for next time.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h3 className='text-base font-semibold mb-6'>Method 2: Using Your Profile Menu</h3>
            <p className='text-sm text-muted-foreground mb-10 leading-6'>
              For more control and to understand your options:
            </p>

            <div className='space-y-6'>
              <div className='relative flex gap-4'>
                <div className='flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg'>
                  1
                </div>
                <div className='absolute left-6 top-12 bottom-0 w-0.5 bg-border -translate-x-1/2' />
                <div className='flex-1 pb-2'>
                  <h4 className='text-lg font-semibold text-foreground mb-2'>Open Your Profile</h4>
                  <p className='text-sm leading-6 text-muted-foreground'>
                    Click on your profile picture or avatar in the sidebar. It is usually near your
                    name.
                  </p>
                </div>
              </div>

              <div className='relative flex gap-4'>
                <div className='flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg'>
                  2
                </div>
                <div className='absolute left-6 top-12 bottom-0 w-0.5 bg-border -translate-x-1/2' />
                <div className='flex-1 pb-2'>
                  <h4 className='text-lg font-semibold text-foreground mb-2'>
                    Find Appearance Settings
                  </h4>
                  <p className='text-sm leading-6 text-muted-foreground'>
                    Look for an option called "Appearance" or "Theme" in the menu that appears.
                  </p>
                </div>
              </div>

              <div className='relative flex gap-4'>
                <div className='flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg'>
                  3
                </div>
                <div className='flex-1 pb-2'>
                  <h4 className='text-lg font-semibold text-foreground mb-2'>Choose Your Theme</h4>
                  <p className='text-sm leading-6 text-muted-foreground'>
                    Select Light, Dark, or System from the available options. The change happens
                    immediately.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Theme Options Section */}
        <section className='mb-20' id='understanding-the-three-theme-options'>
          <h2 className='text-3xl font-bold mb-6'>Understanding the Three Theme Options</h2>

          <div className='space-y-6'>
            <Card className='rounded-xl border shadow-sm'>
              <CardContent className='p-6'>
                <div className='flex items-start gap-4'>
                  <Sun className='h-8 w-8 text-yellow-600 dark:text-yellow-400 flex-shrink-0' />
                  <div className='flex-1'>
                    <h4 className='text-lg font-semibold mb-2'>Light Mode</h4>
                    <p className='text-sm leading-6 text-muted-foreground mb-3'>
                      This is the traditional look: white background with dark text. Great for
                      bright rooms and daytime use.
                    </p>
                    <p className='text-sm text-muted-foreground italic'>
                      Best when: Working in a well-lit office or during daytime hours
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardContent className='p-6'>
                <div className='flex items-start gap-4'>
                  <Moon className='h-8 w-8 text-blue-600 dark:text-blue-400 flex-shrink-0' />
                  <div className='flex-1'>
                    <h4 className='text-lg font-semibold mb-2'>Dark Mode</h4>
                    <p className='text-sm leading-6 text-muted-foreground mb-3'>
                      Dark background with light text. Easier on eyes in low-light conditions and
                      uses less power.
                    </p>
                    <p className='text-sm text-muted-foreground italic'>
                      Best when: Working at night, in dim rooms, or you prefer darker colors
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardContent className='p-6'>
                <div className='flex items-start gap-4'>
                  <Monitor className='h-8 w-8 text-gray-600 dark:text-gray-400 flex-shrink-0' />
                  <div className='flex-1'>
                    <h4 className='text-lg font-semibold mb-2'>System Mode (Automatic)</h4>
                    <p className='text-sm leading-6 text-muted-foreground mb-3'>
                      Vehix automatically matches whatever theme your computer or phone is using.
                      Changes automatically when your device switches between light and dark.
                    </p>
                    <p className='text-sm text-muted-foreground italic'>
                      Best when: You want Vehix to stay in sync with your device's overall
                      appearance
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className='bg-green-50 dark:bg-green-950/30 border-l-4 border-green-600 p-6 rounded-r-xl mt-10'>
            <div className='flex gap-4'>
              <CheckCircle className='h-6 w-6 text-green-600 flex-shrink-0 mt-1' />
              <div>
                <h4 className='text-lg font-semibold text-green-900 dark:text-green-100 mb-2'>
                  Recommended for Most Users
                </h4>
                <p className='text-sm leading-6 text-green-800 dark:text-green-200'>
                  Try using <strong>System Mode</strong> - it is the "set it and forget it" option.
                  If you have set your computer to switch to dark mode at sunset, Vehix will
                  automatically follow along. This way you don't have to remember to switch
                  manually.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* What Changes Section */}
        <section className='mb-20' id='what-changes-when-you-enable-dark-mode'>
          <h2 className='text-3xl font-bold mb-6'>What Changes When You Enable Dark Mode?</h2>
          <p className='text-sm text-muted-foreground mb-10 leading-6'>
            Almost everything in Vehix adapts to your chosen theme:
          </p>

          <div className='grid gap-4 sm:grid-cols-2'>
            <div className='flex gap-3 items-start'>
              <div className='w-2 h-2 bg-primary rounded-full mt-2.5 flex-shrink-0' />
              <div>
                <strong className='text-base'>Backgrounds:</strong>
                <span className='text-base text-muted-foreground ml-2'>
                  White becomes dark gray or black
                </span>
              </div>
            </div>
            <div className='flex gap-3 items-start'>
              <div className='w-2 h-2 bg-primary rounded-full mt-2.5 flex-shrink-0' />
              <div>
                <strong className='text-base'>Text:</strong>
                <span className='text-base text-muted-foreground ml-2'>
                  Dark text becomes light gray or white so you can read it
                </span>
              </div>
            </div>
            <div className='flex gap-3 items-start'>
              <div className='w-2 h-2 bg-primary rounded-full mt-2.5 flex-shrink-0' />
              <div>
                <strong className='text-base'>Cards and Panels:</strong>
                <span className='text-base text-muted-foreground ml-2'>
                  All the boxes and containers adjust their colors
                </span>
              </div>
            </div>
            <div className='flex gap-3 items-start'>
              <div className='w-2 h-2 bg-primary rounded-full mt-2.5 flex-shrink-0' />
              <div>
                <strong className='text-base'>Tables and Lists:</strong>
                <span className='text-base text-muted-foreground ml-2'>
                  The recall list, technician list, and all other tables switch to dark colors
                </span>
              </div>
            </div>
            <div className='flex gap-3 items-start'>
              <div className='w-2 h-2 bg-primary rounded-full mt-2.5 flex-shrink-0' />
              <div>
                <strong className='text-base'>Maps:</strong>
                <span className='text-base text-muted-foreground ml-2'>
                  Even the map visualization changes to a dark style that's easy on your eyes
                </span>
              </div>
            </div>
            <div className='flex gap-3 items-start'>
              <div className='w-2 h-2 bg-primary rounded-full mt-2.5 flex-shrink-0' />
              <div>
                <strong className='text-base'>Forms and Buttons:</strong>
                <span className='text-base text-muted-foreground ml-2'>
                  Input fields and buttons use colors that work well on dark backgrounds
                </span>
              </div>
            </div>
          </div>

          <div className='bg-blue-50 dark:bg-blue-950/30 border-l-4 border-blue-600 p-6 rounded-r-xl mt-10'>
            <div className='flex gap-4'>
              <Info className='h-6 w-6 text-blue-600 flex-shrink-0 mt-1' />
              <div>
                <h4 className='text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2'>
                  Colors Still Make Sense
                </h4>
                <p className='text-sm leading-6 text-blue-800 dark:text-blue-200'>
                  Don't worry - important colors like red for delays, green for completed recalls,
                  and blue for in-progress routes still work the same way in dark mode. We've
                  carefully adjusted them so they are easy to see on dark backgrounds while keeping
                  their meaning clear.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Tips Section */}
        <section className='mb-20' id='tips-for-using-dark-mode'>
          <h2 className='text-3xl font-bold mb-10'>Tips for Using Dark Mode</h2>

          <div className='space-y-10'>
            <div>
              <h3 className='text-lg font-semibold mb-4'>Give Your Eyes Time to Adjust</h3>
              <p className='text-sm leading-6 text-muted-foreground'>
                If you have never used dark mode before, it might feel strange at first. Give it a
                few days of use before deciding if you like it. Your eyes need time to adjust to the
                different contrast.
              </p>
            </div>

            <div>
              <h3 className='text-lg font-semibold mb-4'>Switch Based on Time of Day</h3>
              <p className='text-sm leading-6 text-muted-foreground'>
                Many people use light mode during the day when offices are bright, then switch to
                dark mode in the evening. You can manually switch anytime, or use System Mode to
                handle this automatically.
              </p>
            </div>

            <div>
              <h3 className='text-lg font-semibold mb-4'>Consider Your Environment</h3>
              <p className='text-sm leading-6 text-muted-foreground'>
                Dark mode isn't always better! If you are in a very bright room (like near a window
                in daylight), light mode might actually be easier to see. Experiment with both to
                find what works in your workspace.
              </p>
            </div>
          </div>
        </section>

        {/* Troubleshooting Section */}
        <section className='mb-20' id='troubleshooting'>
          <h2 className='text-3xl font-bold mb-10'>Troubleshooting</h2>

          <div className='space-y-10'>
            <div>
              <h3 className='text-lg font-semibold mb-6'>
                The theme isn't changing when I click the button
              </h3>
              <div className='space-y-6'>
                <div className='relative flex gap-4'>
                  <div className='flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg'>
                    1
                  </div>
                  <div className='absolute left-6 top-12 bottom-0 w-0.5 bg-border -translate-x-1/2' />
                  <div className='flex-1 pb-2'>
                    <h4 className='text-lg font-semibold text-foreground mb-2'>Refresh the Page</h4>
                    <p className='text-sm leading-6 text-muted-foreground'>
                      Press F5 on your keyboard (or Ctrl+R on Windows / ⌘+R on Mac). Sometimes the
                      change needs a refresh to take effect.
                    </p>
                  </div>
                </div>

                <div className='relative flex gap-4'>
                  <div className='flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg'>
                    2
                  </div>
                  <div className='absolute left-6 top-12 bottom-0 w-0.5 bg-border -translate-x-1/2' />
                  <div className='flex-1 pb-2'>
                    <h4 className='text-lg font-semibold text-foreground mb-2'>
                      Check Browser Settings
                    </h4>
                    <p className='text-sm leading-6 text-muted-foreground'>
                      Your browser needs permission to save your preference. Check if you have
                      disabled cookies or local storage for Vehix.
                    </p>
                  </div>
                </div>

                <div className='relative flex gap-4'>
                  <div className='flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg'>
                    3
                  </div>
                  <div className='flex-1 pb-2'>
                    <h4 className='text-lg font-semibold text-foreground mb-2'>Clear Your Cache</h4>
                    <p className='text-sm leading-6 text-muted-foreground'>
                      If refreshing does not help, try clearing your browser cache and cookies, then
                      try again.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className='text-lg font-semibold mb-4'>
                System Mode isn't detecting my device theme
              </h3>
              <p className='text-sm leading-6 text-muted-foreground mb-4'>
                First, make sure your computer or phone actually has a dark mode setting enabled. On
                Windows, this is in Settings → Personalization → Colors. On Mac, it is in System
                Preferences → General → Appearance.
              </p>
              <p className='text-sm leading-6 text-muted-foreground'>
                Some older browsers don't support automatic theme detection. If System Mode does not
                work for you, just manually select Light or Dark instead.
              </p>
            </div>

            <div>
              <h3 className='text-lg font-semibold mb-4'>
                Some parts of the page look wrong in dark mode
              </h3>
              <p className='text-sm leading-6 text-muted-foreground'>
                This might be a bug! Please report it to{' '}
                <a
                  href='mailto:contact@kebi.ai'
                  className='underline hover:no-underline font-semibold text-primary'
                >
                  contact@kebi.ai
                </a>{' '}
                and tell us exactly which page looks wrong. We test dark mode carefully, but
                sometimes things slip through.
              </p>
            </div>
          </div>

          <div className='bg-muted border-l-4 border-border p-6 rounded-r-xl mt-10'>
            <div className='flex gap-4'>
              <FileText className='h-6 w-6 text-muted-foreground flex-shrink-0 mt-1' />
              <div>
                <h4 className='text-lg font-semibold text-foreground mb-2'>Screenshots Help</h4>
                <p className='text-sm leading-6 text-muted-foreground'>
                  If something looks broken in dark mode, taking a screenshot and emailing it to
                  support helps us fix it faster.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Common Questions Section */}
        <section className='mb-20' id='common-questions'>
          <h2 className='text-3xl font-bold mb-10'>Common Questions</h2>

          <div className='space-y-10'>
            <div>
              <h3 className='text-lg font-semibold mb-4'>
                Will my theme choice affect other users?
              </h3>
              <p className='text-sm leading-6 text-muted-foreground'>
                No! Your theme preference is personal and only affects what <em>you</em> see. Every
                person can choose their own favorite theme. If you prefer dark mode but your
                coworker prefers light mode, you can both use what works best for you.
              </p>
            </div>

            <div>
              <h3 className='text-lg font-semibold mb-4'>
                Does dark mode make everything harder to read?
              </h3>
              <p className='text-sm leading-6 text-muted-foreground'>
                Not if it is done well! We've carefully tested dark mode to make sure text is just
                as readable as in light mode. If you find something hard to read, try adjusting your
                screen brightness or let us know so we can improve it.
              </p>
            </div>

            <div>
              <h3 className='text-lg font-semibold mb-4'>
                Can I schedule dark mode to turn on automatically?
              </h3>
              <p className='text-sm leading-6 text-muted-foreground'>
                Yes! Use System Mode and then set your computer or phone to automatically switch to
                dark mode at a certain time (like sunset). Vehix will follow along automatically.
              </p>
            </div>
          </div>

          <div className='bg-green-50 dark:bg-green-950/30 border-l-4 border-green-600 p-6 rounded-r-xl mt-10'>
            <div className='flex gap-4'>
              <CheckCircle className='h-6 w-6 text-green-600 flex-shrink-0 mt-1' />
              <div>
                <h4 className='text-lg font-semibold text-green-900 dark:text-green-100 mb-2'>
                  Try It Now
                </h4>
                <p className='text-sm leading-6 text-green-800 dark:text-green-200'>
                  The fastest way to see if you like dark mode is to just try it! Click the sun/moon
                  icon in the sidebar right now and see how it feels. You can always switch back
                  instantly if you don't like it.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </DocsLayout>
  );
};
